import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, X } from 'lucide-react'
import { useProductsAnySearch } from '@/hooks/useProductsAnySearch'
import type { ProductSearchMatch, MatchedField } from '@/lib/api/products-search'

interface AddToProductProductListProps {
  preselect?: { productId?: string; identifiers?: { sku?: string; gtin?: string; asin?: string } }
  selectedProductId: string | null
  onSelect: (product: ProductSearchMatch) => void
}

function pickInitialIdentifier(ids?: { sku?: string; gtin?: string; asin?: string }): {
  value: string
  field: 'sku' | 'gtin' | 'asin' | null
} {
  if (ids?.sku) return { value: ids.sku, field: 'sku' }
  if (ids?.gtin) return { value: ids.gtin, field: 'gtin' }
  if (ids?.asin) return { value: ids.asin, field: 'asin' }
  return { value: '', field: null }
}

export function AddToProductProductList({
  preselect,
  selectedProductId,
  onSelect,
}: AddToProductProductListProps) {
  const { t } = useTranslation()
  const initial = useMemo(() => pickInitialIdentifier(preselect?.identifiers), [preselect?.identifiers])
  const [query, setQuery] = useState(initial.value)

  const { data, isLoading } = useProductsAnySearch(query)
  const matches = data?.matches ?? []
  const matchedField: MatchedField = data?.matched_field ?? null

  const handleClear = () => setQuery('')

  const preselectId = preselect?.productId
  useEffect(() => {
    if (!preselectId || selectedProductId) return
    const hits = matches.filter((m) => m.id === preselectId)
    if (hits.length === 1) onSelect(hits[0])
  }, [preselectId, selectedProductId, matches, onSelect])

  return (
    <aside className="bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800 flex flex-col overflow-hidden">
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
        <div className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-2">
          {t('addToProduct.productList.label')}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            type="search"
            role="searchbox"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('addToProduct.productList.searchPlaceholder')}
            className="w-full pl-9 pr-8 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100"
          />
          {query.length > 0 && (
            <button
              type="button"
              onClick={handleClear}
              aria-label={t('addToProduct.productList.clearFilter')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {matchedField && matchedField !== 'like' && query.length > 0 && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800 px-2 py-1 rounded-full">
            {t('addToProduct.productList.filteredBy', { type: matchedField.toUpperCase() })}
          </div>
        )}
      </div>

      <div
        className="text-xs text-neutral-500 px-4 py-2 border-b border-neutral-100 dark:border-neutral-800"
        role="status"
        aria-live="polite"
      >
        {isLoading ? '…' : t('addToProduct.productList.count', { count: matches.length })}
      </div>

      {matches.length === 0 && query.length > 0 && !isLoading ? (
        <div className="px-4 py-6 text-sm text-neutral-500">
          {t('addToProduct.productList.empty')}
        </div>
      ) : (
        <ul className="flex-1 overflow-y-auto">
          {matches.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onSelect(p)}
                className={`w-full text-left px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 ${
                  selectedProductId === p.id
                    ? 'bg-primary-50 dark:bg-primary-950/30 border-l-2 border-primary-500'
                    : ''
                }`}
              >
                <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                  {p.name}
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  {p.sku ?? p.gtin ?? p.asin ?? '—'}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  )
}
